// @flow

import React from 'react'
import dayjs from 'dayjs'
import { Link } from 'gatsby'
import { markdown } from 'markdown'
import Cta from './Cta'

const ConferenceImage = ({ className, color }) => (
  <svg className={className} viewBox="0 0 119.57 121.68" xmlns="http://www.w3.org/2000/svg"><g fill="none" stroke={color} strokeMiterlimit="10" strokeWidth="4"><path d="m12.76 64.7c-2.88 0-5.23-1.76-5.23-3.92v-54.86c0-2.16 2.35-3.92 5.23-3.92h94.05c2.87 0 5.19 1.76 5.19 3.92v54.86c0 2.16-2.35 3.92-5.22 3.92z"/><circle cx="59.78" cy="21.16" r="8.85"/><path d="m43.8 54.45v-12.45s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.45"/><circle cx="59.78" cy="88.39" r="8.85"/><path d="m43.8 121.68v-12.42s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.42"/><circle cx="17.98" cy="88.39" r="8.85"/><path d="m2 121.68v-12.42s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.42"/><circle cx="101.58" cy="88.39" r="8.85"/><path d="m85.6 121.68v-12.42s.08-8.68 8.68-8.68h14.6s8.69 0 8.69 8.68v12.42"/></g></svg>
)

type SpeakerType = {
  id: string,
  name: string,
  description: string,
}

type ProgramItemItemType = {
  key: string,
  description: string,
  speaker: string,
  startTime: string,
  speakers: Array<SpeakerType>,
}

type Props = {
  id: string,
  number: number,
  session: string,
  date: string,
  bgColor: string,
  color: string,
  title: string,
  description: string,
  ctaText: string,
  url?: string,
  ctaURL: String,
  items: Array<ProgramItemItemType>,
  highlight?: boolean,
}

const ProgramItem = ({
  number,
  date,
  bgColor,
  color,
  session,
  title,
  description,
  ctaText,
  ctaURL,
  url,
  items,
  highlight
}: Props) => {
  const localeDate = dayjs(date).format("DD-MM-YYYY")
  const itemURL = url || ctaURL

  const Content = () => (
    <div className={`flex flex-col h-full ${highlight ? "shadow-xl md:mx-auto" : "col-span-1"}`}>
      <div className={`h-4 border-b-4 border-dashed border-light-gray ${bgColor || "bg-white"}`} />
      <div key="content" className={`flex-1 pt-6 pb-12 md:pb-8 ${bgColor || "bg-white"}`}>
        <div
          key="image-container"
          className="flex flex-col items-center justify-center px-5 pb-6 text-center md:text-left md:px-8 md:flex-row md:items-start md:justify-start"
          >
            <span className="block text-xl font-black leading-tight md:hidden" style={{ color: `#${color}` }}>{session} {number}.</span>
            <div className="my-6 md:mt-2 flex-0 md:mb-0 md:mr-4">
              <ConferenceImage className="w-20 h-20" color={`#${color}`} />
            </div>
            <div className="text-base md:text-sm">
              <span className="hidden text-xl font-black leading-tight md:block" style={{ color: `#${color}` }}>{session} {number}.</span>
              <span className={`block font-black ${highlight ? "leading-tight text-xl" : ""}`}>{title}</span>
              <span className="block">{localeDate}</span>
              {description && 
               <span dangerouslySetInnerHTML={{ __html: markdown.toHTML(description) }} />
              }
            </div>
        </div>
        <div className="w-full h-1 px-5 mb-6 md:hidden">
          <div className="w-full h-full mx-auto bg-light-gray" />
        </div>
        <ul
          key="list-container"
          className="px-5 text-base text-center md:text-sm md:pl-5 md:pr-8 md:text-left"
        >
          {items && items.map(item => {
            // Handle speakers markdown
            if (item.speakers) {
              const itemSpeakers = item.speakers.map((speaker, index) => {
                if (index === item.speakers.length - 1) {
                  return `**${speaker.name}**`
                }
                return `**${speaker.name}** i `
              })
              const spread = `${[...itemSpeakers]}`
              const speakersMarkdown = spread.replace(",", "")
  
              return (
                <li key={item.key} className="flex items-stretch justify-start">
                  <div key="item-bullet-container" className="items-start justify-start hidden py-1 mr-2 md:flex">
                    <div key="item-bullet" className="w-1 h-1 my-1" style={{ backgroundColor: `#${color}` }} />
                  </div>
                  {item.description &&
                    <span className="flex-1 block mb-3" dangerouslySetInnerHTML={{ __html: markdown.toHTML(`${item.startTime || ''}. ${item.description} ${speakersMarkdown}`) }}/>
                  }
                </li>
              )
            }
          })}
        </ul>
        {(typeof ctaURL === "string" && ctaText !== "") && (
          <div
              key="cta-wrapper"
              className="flex items-stretch justify-center px-5 pt-6 md:pt-6 md:px-8 md:block"
            >
              <Cta ctaText={ctaText} ctaURL={ctaURL} color={color} isDisabled={ctaURL === ""} />
            </div>
        )}
      </div>
    </div>
  )


  if (itemURL) {
    if (itemURL.charAt(0) === "/") return (
      <Link to={itemURL}>
        <Content />
      </Link>
    )
    if (itemURL.charAt(0) !== "/") return (
      <a href={itemURL}>
        <Content />
      </a>
    )
  }

  return (
    <Content />
  )
}

export default ProgramItem
